import { useCallback } from 'react'
import { isCurrentRequestBot } from 'actions/SystemActions'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { canUseGuestCheckout, type GuestCheckoutGuardInput } from 'lib/checkout/guestCheckout'

type GuestCheckoutPurchase = Pick<GuestCheckoutGuardInput, 'isWalledGarden' | 'isMultiRoom' | 'itemTypes'>

interface GuestCheckoutEligibility {
  /** Whether this exact purchase can proceed to checkout without signing in. */
  canProceedAsGuest: (purchase: GuestCheckoutPurchase) => boolean
  /** Fires the experiment activation event for an eligible purchase. */
  activate: (purchase: GuestCheckoutPurchase) => void
}

export function shouldEnableGuestCheckout(
  isEligiblePurchase: boolean,
  variation: string | undefined,
): boolean {
  return isEligiblePurchase && variation === 'on'
}

/**
 * Applies the rollout controls to the shared guest checkout guard. Product
 * flows supply the exact purchase shape because it is not available globally
 * before they initialise a cart.
 */
function useGuestCheckoutEligibility(isLoggedIn: boolean | undefined): GuestCheckoutEligibility {
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const utmBp = useAppSelector(state => state.utm.bp)
  const isBotTraffic = useAppSelector(isCurrentRequestBot)
  const { triggerActivation, variation } = useExperiment(
    Experiments.growthMetasearchGuestCheckout,
    { canActivate: false },
  )

  const isEligiblePurchase = useCallback((purchase: GuestCheckoutPurchase) => canUseGuestCheckout({
    ...purchase,
    brand: config.BRAND,
    isBotTraffic,
    isLoggedIn: !!isLoggedIn,
    regionCode,
    utmBp,
  }), [isBotTraffic, isLoggedIn, regionCode, utmBp])

  const canProceedAsGuest = useCallback((purchase: GuestCheckoutPurchase) => {
    const eligiblePurchase = isEligiblePurchase(purchase)
    const canProceed = shouldEnableGuestCheckout(
      eligiblePurchase,
      variation,
    )
    return canProceed
  }, [isEligiblePurchase, variation])

  const activate = useCallback((purchase: GuestCheckoutPurchase) => {
    if (isEligiblePurchase(purchase)) {
      triggerActivation()
    }
  }, [isEligiblePurchase, triggerActivation])

  return { canProceedAsGuest, activate }
}

export default useGuestCheckoutEligibility
